// src/services/FCMService.js
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { getMessaging, getToken } from 'firebase/messaging';
import { initializeApp, getApps } from 'firebase/app';

let loggedAwaitingPermission = false;

class FCMService {
  constructor() {
    this.token = null;
    this.isInitialized = false;
    this.subscribedTopics = new Set();
    this.messaging = null;
  }

  /**
   * Initialize FCM service
   */
  async initialize() {
    if (this.isInitialized) return;
    
    try {
      // Initialize FCM for both native and web platforms
      if (Capacitor.isNativePlatform()) {
        // Native platform - use Capacitor Push Notifications
        await this.requestPermissions();
        await this.registerListeners();
        await this.getToken();
        this.isInitialized = true;
        console.log('FCM Service initialized successfully (Native)');
      } else {
        // Web platform - use Firebase Web SDK
        await this.initializeWebFCM();
        this.isInitialized = true;
        console.log('FCM Service initialized successfully (Web)');
      }
    } catch (error) {
      console.error('FCM initialization failed:', error);
    }
  }

  /**
   * Initialize FCM for web platforms
   */
  async initializeWebFCM() {
    try {
      // Check if Firebase is already initialized
      if (getApps().length === 0) {
        console.warn('Firebase not initialized. FCM topics will not work on web.');
        return;
      }

      // Check if notifications are supported
      if (!('Notification' in window)) {
        console.warn('This browser does not support notifications');
        return;
      }

      // Get Firebase messaging instance
      this.messaging = getMessaging();
      
      // Do NOT request permission automatically. This must be user-initiated.
      // If permission is already granted (e.g., returning user), fetch token immediately.
      const permission = Notification.permission;
      if (permission === 'granted') {
        try {
          const vapidKey = (import.meta?.env?.VITE_FIREBASE_VAPID_KEY || '').trim();
          const token = vapidKey ? await getToken(this.messaging, { vapidKey }) : await getToken(this.messaging);
          this.token = token;
          console.log('Web FCM Token (existing grant):', token);
        } catch (tokenError) {
          console.warn('Could not get FCM token (existing grant):', tokenError);
        }
      } else if (!loggedAwaitingPermission) {
        // Leave it to a user action via requestBrowserPermission(), but keep the console quiet.
        loggedAwaitingPermission = true;
        console.debug('Web push permission not granted yet; waiting for user interaction to request permission.');
      }
    } catch (error) {
      console.error('Web FCM initialization failed:', error);
      // Don't throw - app should still work without FCM
    }
  }

  /**
   * Ask for browser notification permission on a USER GESTURE, then get token
   */
  async requestBrowserPermission() {
    try {
      if (!('Notification' in window)) {
        console.warn('Notifications API not supported');
        return { granted: false };
      }
      // Ensure messaging is set up
      if (!this.messaging) {
        try {
          if (getApps().length > 0) this.messaging = getMessaging();
        } catch {}
      }
      const result = await Notification.requestPermission();
      if (result === 'granted' && this.messaging) {
        try {
          const vapidKey = (import.meta?.env?.VITE_FIREBASE_VAPID_KEY || '').trim();
          const token = vapidKey ? await getToken(this.messaging, { vapidKey }) : await getToken(this.messaging);
          this.token = token;
          console.log('Web FCM Token (on user grant):', token);
          // Optional: send token to server
          await this.sendTokenToServer(token);
          return { granted: true, token };
        } catch (tokenError) {
          console.warn('Permission granted but failed to get FCM token:', tokenError);
          return { granted: true, token: null };
        }
      }
      return { granted: false };
    } catch (err) {
      console.warn('Notification permission request failed:', err);
      return { granted: false };
    }
  }

  /**
   * Request push notification permissions
   */
  async requestPermissions() {
    try {
      const permStatus = await PushNotifications.requestPermissions();
      
      if (permStatus.receive === 'granted') {
        console.log('Push notification permissions granted');
        return true;
      } else {
        console.warn('Push notification permissions denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting permissions:', error);
      return false;
    }
  }

  /**
   * Register FCM listeners
   */
  async registerListeners() {
    // On registration, we get the FCM token
    PushNotifications.addListener('registration', (token) => {
      console.log('FCM Registration token:', token.value);
      this.token = token.value;
      this.sendTokenToServer(token.value);
    });

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Error on registration:', error);
    });

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push received:', notification);
      this.handleNotificationReceived(notification);
    });

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('Push action performed:', notification);
      this.handleNotificationAction(notification);
    });
  }

  /**
   * Get FCM token
   */
  async getToken() {
    try {
      if (Capacitor.isNativePlatform()) {
        const result = await PushNotifications.getToken();
        this.token = result.value;
        console.log('FCM Token:', this.token);
        return this.token;
      }
      return null;
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  /**
   * Get current FCM token
   */
  getCurrentToken() {
    return this.token;
  }

  /**
   * Send FCM token to your server
   */
  async sendTokenToServer(token) {
    try {
      // TODO: Replace with your actual server endpoint
      console.log('Sending FCM token to server:', token);
      
      // Example: Send to your Firebase Functions or backend API
      // const response = await fetch('https://your-api.com/fcm-token', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({
      //     token: token,
      //     userId: getCurrentUserId(), // Your user ID
      //     platform: Capacitor.getPlatform()
      //   })
      // });
      
      // For now, just log it
      console.log('FCM Token ready for server:', token);
    } catch (error) {
      console.error('Error sending token to server:', error);
    }
  }

  /**
   * Handle notification received while app is open
   */
  handleNotificationReceived(notification) {
    // You can show an in-app notification here
    console.log('Notification received while app is open:', notification);
    
    // Example: Show a toast or update UI
    // showToast(notification.notification.title, notification.notification.body);
  }

  /**
   * Handle notification action (when user taps notification)
   */
  handleNotificationAction(notification) {
    const data = notification.notification.data;
    console.log('Notification action data:', data);
    
    // Handle different notification types
    if (data) {
      switch (data.type) {
        case 'order_update':
          // Navigate to order details
          if (data.orderId) {
            window.location.href = `/orders/${data.orderId}`;
          }
          break;
        case 'delivery_status':
          // Navigate to delivery tracking
          if (data.orderId) {
            window.location.href = `/delivery/${data.orderId}`;
          }
          break;
        case 'promotion':
          // Navigate to promotions page
          window.location.href = '/promotions';
          break;
        default:
          // Navigate to home or specific URL
          if (data.url) {
            window.location.href = data.url;
          } else {
            window.location.href = '/';
          }
      }
    }
  }

  /**
   * Subscribe to FCM topic
   */
  async subscribeToTopic(topic) {
    try {
      console.log(`Subscribing to topic: ${topic}`);
      
      // Store the topic locally
      this.subscribedTopics.add(topic);
      
      if (Capacitor.isNativePlatform()) {
        // Native platform - send to backend for subscription
        await this.sendTopicSubscriptionToServer(topic, 'subscribe');
      } else {
        // Web platform - topics work differently, just log for now
        console.log(`Web platform: Topic subscription logged for backend processing`);
      }
      
      console.log(`Successfully subscribed to topic: ${topic}`);
      return true;
    } catch (error) {
      console.error(`Error subscribing to topic ${topic}:`, error);
      return false;
    }
  }

  /**
   * Unsubscribe from FCM topic
   */
  async unsubscribeFromTopic(topic) {
    try {
      console.log(`Unsubscribing from topic: ${topic}`);
      
      // Remove the topic locally
      this.subscribedTopics.delete(topic);
      
      if (Capacitor.isNativePlatform()) {
        // Native platform - send to backend for unsubscription
        await this.sendTopicSubscriptionToServer(topic, 'unsubscribe');
      } else {
        // Web platform - topics work differently, just log for now
        console.log(`Web platform: Topic unsubscription logged for backend processing`);
      }
      
      console.log(`Successfully unsubscribed from topic: ${topic}`);
      return true;
    } catch (error) {
      console.error(`Error unsubscribing from topic ${topic}:`, error);
      return false;
    }
  }

  /**
   * Get list of subscribed topics
   */
  getSubscribedTopics() {
    return Array.from(this.subscribedTopics);
  }

  /**
   * Subscribe to multiple topics at once
   */
  async subscribeToTopics(topics) {
    const results = await Promise.allSettled(
      topics.map(topic => this.subscribeToTopic(topic))
    );
    
    const successful = results
      .map((result, index) => result.status === 'fulfilled' ? topics[index] : null)
      .filter(Boolean);
    
    console.log(`Successfully subscribed to topics: ${successful.join(', ')}`);
    return successful;
  }

  /**
   * Send topic subscription to your backend
   */
  async sendTopicSubscriptionToServer(topic, action) {
    try {
      const token = this.getCurrentToken();
      if (!token) {
        console.warn('No FCM token available for topic subscription');
        return;
      }

      // TODO: Replace with your actual server endpoint
      console.log(`Sending ${action} request for topic ${topic} to server`);
      
      // Example: Send to your Firebase Functions or backend API
      // const response = await fetch('https://your-api.com/fcm-topics', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({
      //     token: token,
      //     topic: topic,
      //     action: action, // 'subscribe' or 'unsubscribe'
      //     platform: Capacitor.getPlatform()
      //   })
      // });
      
      // For now, just log it
      console.log(`Topic ${action} request ready for server:`, {
        token,
        topic,
        action,
        platform: Capacitor.getPlatform()
      });
    } catch (error) {
      console.error(`Error sending topic ${action} to server:`, error);
    }
  }

  /**
   * Auto-subscribe to user role topics
   */
  async subscribeToUserRoleTopics(userRoles = []) {
    const roleTopics = [];
    
    // Map user roles to topics
    if (userRoles.includes('admin')) {
      roleTopics.push('admins', 'all_users');
    }
    if (userRoles.includes('delivery_rider')) {
      roleTopics.push('delivery_riders', 'all_users');
    }
    if (userRoles.includes('store_manager')) {
      roleTopics.push('store_managers', 'all_users');
    }
    if (userRoles.includes('packer')) {
      roleTopics.push('packers', 'all_users');
    }
    if (userRoles.includes('coop_member')) {
      roleTopics.push('coop_members', 'all_users');
    }
    
    // Always subscribe to general topics
    roleTopics.push('general_announcements');
    
    if (roleTopics.length > 0) {
      await this.subscribeToTopics(roleTopics);
    }
  }

  /**
   * Cleanup FCM service
   */
  cleanup() {
    this.token = null;
    this.isInitialized = false;
    this.subscribedTopics.clear();
    this.messaging = null;
    console.log('FCM Service cleaned up');
  }
}

// Export singleton instance
export const fcmService = new FCMService();
export default fcmService;
